import useRisksmartUser from '@risksmart-app/components/src/hooks/useRisksmartUser';
import Table from '@risksmart-app/components/src/table';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ObjectToolbar } from 'src/blocks/object-toolbar';
import { PageLayout } from 'src/layouts';

import { useGetPublicDocumentFiles } from '@/hooks/queries';
import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';

import ViewSelector from '../../../components/view-selector';
import CardView from './CardView';
import { useGetCollectionTableProps } from './config';

const PublicPoliciesPage = () => {
  const { user } = useRisksmartUser();
  const { t } = useTranslation(['common'], { keyPrefix: 'publicPolicies' });
  const objectToolbarEnabled = useIsFeatureFlagEnabled('dynamic_forms');
  const title = t('register_title');
  const { data, loading } = useGetPublicDocumentFiles({
    queryArgs: { userId: user?.userId! },
    shouldSkip: !user?.userId,
  });

  type View = 'card' | 'table';
  const viewOptions: { text: string; id: View }[] = [
    { text: t('viewSelector.table'), id: 'table' },
    { text: t('viewSelector.card'), id: 'card' },
  ];

  const collectionProps = useGetCollectionTableProps(data?.document_file);
  const [selectedView, setSelectedView] = useState<View>('table');

  return (
    <PageLayout
      title={title}
      // Title-only object toolbar (CUS-166) — flag-gated. Public policies has no
      // register actions, so the toolbar mirrors the empty legacy actions slot.
      headerOverride={
        objectToolbarEnabled ? (
          <ObjectToolbar config={{ header: { title } }} />
        ) : undefined
      }
      actions={<></>}
    >
      {selectedView === 'table' && (
        <Table
          {...collectionProps}
          loading={loading}
          header={
            <ViewSelector<View>
              selectedView={selectedView}
              onSelectedViewChanged={(view) => {
                collectionProps.actions.setSorting?.({
                  isDescending: true,
                  sortingColumn: {
                    sortingField: 'ModifiedAtTimestamp',
                  },
                });
                setSelectedView(view);
              }}
              options={viewOptions}
            />
          }
        />
      )}
      {selectedView === 'card' && (
        <CardView
          pagination={collectionProps.pagination}
          items={collectionProps.items}
          empty={collectionProps.empty}
          filter={collectionProps.filter}
          header={
            <ViewSelector<View>
              selectedView={selectedView}
              onSelectedViewChanged={setSelectedView}
              options={viewOptions}
            />
          }
        />
      )}
    </PageLayout>
  );
};

export default PublicPoliciesPage;
